
import React, { useState } from 'react';
import { BrandLogo, Button } from './Components';
import { useApp } from './AppContext';
import { usePwaInstall, InstallPwaModal } from './InstallPwa';

export const Header: React.FC<{ page: string; setPage: (p: string) => void }> = ({ page, setPage }) => {
  const { user, logout, notifications, clearNotification } = useApp();
  const [showNotifications, setShowNotifications] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isStandalone, showModal, openModal, closeModal, triggerInstall, hasNativePrompt } = usePwaInstall();

  const handleNavClick = (p: string) => {
    window.location.hash = p;
    setPage(p);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePatientTabClick = (tabName: string, action?: string) => {
    const targetHash = action ? `dashboard?action=${action}` : `dashboard?tab=${tabName}`;
    window.location.hash = targetHash;
    setPage('dashboard');
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    }
  };

  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'leadership', label: 'About Us' },
    { id: 'doctors', label: 'Doctors' },
    ...(user ? [{ id: 'dashboard', label: user.role === 'doctor' ? 'Practitioner Portal' : user.role === 'admin' ? 'Admin Core' : 'My Portal' }] : []),
    { id: 'contact', label: 'Help' }
  ];

  return (
    <>
      <nav aria-label="Main Navigation" className="bg-white/90 backdrop-blur-2xl sticky top-0 z-[100] border-b border-indigo-50 h-20 md:h-24 transition-all">
        <div className="max-w-7xl mx-auto px-4 h-full flex justify-between items-center">
          <button onClick={() => handleNavClick('home')} className="cursor-pointer active:scale-95 transition-transform scale-75 md:scale-100 origin-left focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-2xl" aria-label="ROVITA Telemedicine Home Page">
            <BrandLogo hideTagline={true} />
          </button>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8 xl:gap-10">
            {navItems.map(p => (
              <button 
                key={p.id} 
                onClick={() => handleNavClick(p.id)} 
                aria-current={page === p.id ? 'page' : undefined}
                className={`font-black uppercase text-xs tracking-[0.25em] transition-all hover:text-indigo-600 relative group focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-lg px-2 py-1 ${page === p.id ? 'text-indigo-600 font-extrabold' : 'text-slate-500'}`}
              >
                {p.label}
                <span className={`absolute -bottom-2 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-indigo-600 transition-all duration-300 ${page === p.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}></span>
              </button>
            ))}

            <div className="h-8 w-px bg-slate-100 mx-1" aria-hidden="true"></div>
          
          {user && (
            <div className="relative">
                <button 
                    onClick={() => setShowNotifications(!showNotifications)}
                    aria-label={`Notifications and alerts, ${notifications?.length || 0} items`}
                    aria-expanded={showNotifications}
                    aria-haspopup="true"
                    className="w-11 h-11 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center relative hover:bg-indigo-600 hover:text-white transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-2xs"
                >
                    <i className="fa-solid fa-bell text-base" aria-hidden="true"></i>
                    {notifications && notifications.length > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white text-[10px] font-black rounded-full flex items-center justify-center border-2 border-white">
                            {notifications.length}
                        </span>
                    )}
                </button>

                {showNotifications && (
                    <div role="region" aria-label="Notifications Panel" className="absolute top-16 right-0 w-80 bg-white rounded-[2rem] shadow-2xl border border-slate-100 py-6 z-[110] animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="px-6 pb-4 border-b border-slate-50 flex justify-between items-center">
                            <h4 className="text-xs font-black uppercase tracking-widest text-slate-400">Your Alerts</h4>
                            <button onClick={() => setShowNotifications(false)} aria-label="Close notifications panel" className="text-slate-300 hover:text-slate-500"><i className="fa-solid fa-xmark text-lg" aria-hidden="true"></i></button>
                        </div>
                        <div className="max-h-96 overflow-y-auto scrollbar-hide">
                            {!notifications || notifications.length === 0 ? (
                                <div className="p-10 text-center">
                                    <p className="text-xs font-black uppercase tracking-widest text-slate-300">No new alerts</p>
                                </div>
                            ) : (
                                notifications.map(n => (
                                    <div key={n.id} className="p-6 border-b border-slate-50 hover:bg-slate-50 transition-colors relative group">
                                        <p className="text-sm font-bold text-slate-700 leading-relaxed mb-2">{n.message}</p>
                                        <p className="text-[10px] font-black uppercase text-indigo-400">{new Date(n.timestamp).toLocaleTimeString()}</p>
                                        <button 
                                            onClick={() => clearNotification(n.id)}
                                            aria-label="Dismiss this alert notification"
                                            className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity text-slate-300 hover:text-rose-500"
                                        >
                                            <i className="fa-solid fa-circle-check text-lg" aria-hidden="true"></i>
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>
          )}

          {user ? (
            <div className="flex items-center gap-4">
              <button onClick={() => handleNavClick('dashboard')} aria-label={`Open portal dashboard for ${user.name}`} className="group flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-2xl p-1 bg-slate-50/80 hover:bg-indigo-50/80 transition-all border border-slate-100 pr-3">
                <img src={user.avatar} className="w-10 h-10 rounded-xl bg-indigo-50 border border-white shadow-2xs group-hover:scale-105 transition-all object-cover" alt={`Avatar of ${user.name}`} loading="lazy" />
                <div className="text-left">
                  <p className="text-slate-900 font-black text-xs leading-none mb-1">{user.name}</p>
                  <p className="text-[9px] font-bold text-indigo-600 uppercase tracking-wider">
                    {user.role === 'doctor' ? 'Doctor Portal' : user.role === 'admin' ? 'Admin' : 'Patient Portal'}
                  </p>
                </div>
              </button>
              <button onClick={() => { logout(); handleNavClick('home'); }} aria-label="Log out of account" title="Log Out" className="w-10 h-10 bg-rose-50 text-rose-500 rounded-xl hover:bg-rose-500 hover:text-white transition-all flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-rose-500 shadow-2xs"><i className="fa-solid fa-power-off text-sm" aria-hidden="true"></i></button>
            </div>
          ) : (
            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => handleNavClick('login')} ariaLabel="Sign into your account" className="py-2.5 px-4 text-sm font-bold">Sign In</Button>
              <Button onClick={() => handleNavClick('register')} ariaLabel="Register new account" className="py-2.5 px-5 text-sm font-black shadow-sm">Join Now</Button>
            </div>
          )}
        </div>

        {/* Mobile Menu Toggle */}
        <button 
            className="lg:hidden w-11 h-11 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center shadow-sm active:scale-95 transition-transform focus:outline-none focus:ring-2 focus:ring-indigo-500"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-label="Toggle navigation menu"
            aria-controls="mobile-menu-drawer"
        >
            <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-lg`} aria-hidden="true"></i>
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div id="mobile-menu-drawer" role="region" aria-label="Mobile Navigation Menu" className="lg:hidden absolute top-20 left-0 w-full bg-white/95 backdrop-blur-xl border-b border-slate-100 shadow-2xl p-6 flex flex-col gap-6 animate-in slide-in-from-top-5 duration-300 z-50 h-[calc(100vh-80px)] overflow-y-auto">
            {user ? (
                <div className="space-y-3">
                    <button className="flex items-center gap-4 p-4 bg-indigo-50 hover:bg-indigo-100/70 rounded-2xl text-left w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors" onClick={() => handleNavClick('dashboard')} aria-label={`Open portal dashboard for ${user.name}`}>
                        <img src={user.avatar} className="w-12 h-12 rounded-xl bg-white object-cover shadow-sm" alt={`Avatar of ${user.name}`} loading="lazy" />
                        <div className="flex-grow">
                            <p className="font-black text-slate-900 text-base sm:text-lg">{user.name}</p>
                            <p className="text-[10px] font-black uppercase text-indigo-600 tracking-wider">
                                {user.role === 'doctor' ? 'Practitioner Portal' : user.role === 'admin' ? 'Admin Portal' : 'Patient Portal'}
                            </p>
                        </div>
                        <i className="fa-solid fa-chevron-right text-indigo-400" aria-hidden="true"></i>
                    </button>

                    {user.role === 'patient' && (
                        <div className="grid grid-cols-2 gap-2 p-2 bg-slate-50 rounded-2xl border border-slate-100">
                            <button 
                                onClick={() => handlePatientTabClick('appointments')}
                                className="p-3 bg-white hover:bg-indigo-50 rounded-xl text-left text-xs font-bold text-slate-700 hover:text-indigo-600 shadow-sm border border-slate-100 flex items-center gap-2 transition-all"
                            >
                                <i className="fa-solid fa-calendar-check text-rose-500"></i>
                                My Bookings
                            </button>
                            <button 
                                onClick={() => handlePatientTabClick('vault')}
                                className="p-3 bg-white hover:bg-indigo-50 rounded-xl text-left text-xs font-bold text-slate-700 hover:text-indigo-600 shadow-sm border border-slate-100 flex items-center gap-2 transition-all"
                            >
                                <i className="fa-solid fa-folder-open text-purple-500"></i>
                                View Reports
                            </button>
                            <button 
                                onClick={() => handlePatientTabClick('doctor-notes')}
                                className="p-3 bg-white hover:bg-indigo-50 rounded-xl text-left text-xs font-bold text-slate-700 hover:text-indigo-600 shadow-sm border border-slate-100 flex items-center gap-2 transition-all"
                            >
                                <i className="fa-solid fa-prescription text-emerald-500"></i>
                                Doctor Notes & Rx
                            </button>
                            <button 
                                onClick={() => handlePatientTabClick('vitals')}
                                className="p-3 bg-white hover:bg-indigo-50 rounded-xl text-left text-xs font-bold text-slate-700 hover:text-indigo-600 shadow-sm border border-slate-100 flex items-center gap-2 transition-all"
                            >
                                <i className="fa-solid fa-id-card text-sky-500"></i>
                                Health ID & Vitals
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex gap-3">
                    <Button onClick={() => handleNavClick('login')} ariaLabel="Sign In" className="flex-1 py-4 text-lg">Sign In</Button>
                    <Button onClick={() => handleNavClick('register')} ariaLabel="Register Account" variant="outline" className="flex-1 py-4 text-lg">Register</Button>
                </div>
            )}
            
            <div className="space-y-2">
                {[
                    { id: 'home', label: 'Home', icon: 'fa-house' },
                    { id: 'leadership', label: 'About Us', icon: 'fa-users' },
                    { id: 'doctors', label: 'Doctors', icon: 'fa-user-doctor' },
                    ...(user ? [{ id: 'dashboard', label: 'My Portal / Dashboard', icon: 'fa-gauge-high' }] : []),
                    { id: 'contact', label: 'Help & Support', icon: 'fa-circle-question' }
                ].map(p => (
                    <button 
                        key={p.id} 
                        onClick={() => handleNavClick(p.id)} 
                        aria-current={page === p.id ? 'page' : undefined}
                        className="w-full text-left p-4 rounded-xl font-bold text-slate-600 hover:bg-indigo-50 hover:text-indigo-600 transition-all flex items-center gap-4 text-base focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                        <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center text-indigo-600">
                             <i className={`fa-solid ${p.icon}`} aria-hidden="true"></i>
                        </div>
                        {p.label}
                    </button>
                ))}
            </div>

            {user && (
                 <div className="pt-4 border-t border-slate-100 flex gap-4">
                     <button onClick={() => { logout(); handleNavClick('home'); }} aria-label="Log out of account" className="flex-1 py-4 bg-rose-50 text-rose-500 font-black rounded-xl uppercase text-sm tracking-widest flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-rose-500">
                        <i className="fa-solid fa-power-off" aria-hidden="true"></i> Log Out
                     </button>
                 </div>
            )}
        </div>
      )}
    </nav>
    </>
  );
};

export const Footer: React.FC<{ setPage: (p: string) => void }> = ({ setPage }) => {
  const { openModal } = usePwaInstall();

  return (
  <footer className="bg-indigo-600 text-white py-20 md:py-32 relative overflow-hidden">
    <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/50 to-transparent"></div>
    <div className="max-w-7xl mx-auto px-6 relative z-10">
      <div className="grid md:grid-cols-3 gap-12 md:gap-20 border-b border-white/10 pb-16 md:pb-24 text-center md:text-left">
        <div className="flex flex-col items-center md:items-start">
          <BrandLogo textColor="text-white" className="mb-6 opacity-100" />
          <p className="text-white/80 font-normal max-w-sm text-lg leading-relaxed">
            Helping you connect with world-class doctors from the comfort of your home.
          </p>
        </div>
        <div>
          <h4 className="text-white/50 font-semibold text-xs uppercase tracking-widest mb-6">Company</h4>
          <ul className="space-y-4 text-[15px] font-medium text-white/90">
            <li><button onClick={() => setPage('leadership')} aria-label="View Our Leadership Team" className="hover:text-white transition-all duration-300">Our Team</button></li>
            <li><button onClick={() => setPage('contact')} aria-label="Contact Patient Support" className="hover:text-white transition-all duration-300">Contact Us</button></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white/50 font-semibold text-xs uppercase tracking-widest mb-6">Helpful Links</h4>
          <ul className="space-y-4 text-[15px] font-medium text-white/90">
            <li><button onClick={() => setPage('faqs')} aria-label="View Frequently Asked Questions" className="hover:text-white transition-all duration-300">FAQs</button></li>
            <li><button onClick={() => setPage('terms')} aria-label="View Terms of Service" className="hover:text-white transition-all duration-300">Terms of Service</button></li>
            <li><button onClick={() => setPage('privacy')} aria-label="View Privacy Policy and HIPAA Compliance" className="hover:text-white transition-all duration-300">Privacy Policy</button></li>
            <li><button onClick={() => setPage('ethics')} aria-label="View Clinical Ethics Guidelines" className="hover:text-white transition-all duration-300">Ethics</button></li>
            <li>
              <button 
                onClick={openModal} 
                aria-label="Install ROVITA App to Home Screen"
                className="hover:text-white transition-all duration-300 flex items-center gap-2 text-indigo-200 hover:text-white font-bold"
              >
                <i className="fa-solid fa-mobile-screen-button text-xs" aria-hidden="true"></i>
                Add to Home Screen
              </button>
            </li>
          </ul>
        </div>
      </div>
      <div className="pt-10 flex flex-col md:flex-row justify-between items-center gap-6">
        <p className="text-white/40 text-[11px] font-semibold tracking-wider uppercase">&copy; 2024 ROVITA</p>
        <div className="flex gap-8 text-white/40 text-[11px] font-semibold tracking-wider uppercase">
          <button onClick={() => setPage('privacy')} aria-label="View Privacy Policy" className="hover:text-white transition-colors">Privacy</button>
          <button onClick={() => setPage('terms')} aria-label="View Terms of Service" className="hover:text-white transition-colors">Terms</button>
        </div>
      </div>
    </div>
  </footer>
  );
};
